import axios from 'axios';
import { API_BASE_URL, WS_BASE_URL } from './apiConfig';

export const AUTH_TOKEN_KEY = 'auth_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const AUTH_TOKEN_CHANGE_EVENT = 'mt5:auth-token-change';

const authTokenListeners = new Set();

const getStorage = () => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
};

export const getAccessToken = () => getStorage()?.getItem(AUTH_TOKEN_KEY) || '';
export const getRefreshToken = () => getStorage()?.getItem(REFRESH_TOKEN_KEY) || '';

const emitAuthTokenChange = (reason = 'updated') => {
  const detail = {
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),
    reason,
  };

  authTokenListeners.forEach(listener => {
    try {
      listener(detail);
    } catch {
      // A consumer must not prevent other token listeners from running.
    }
  });

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new CustomEvent(AUTH_TOKEN_CHANGE_EVENT, { detail }));
    } catch {
      // CustomEvent may be unavailable in non-browser test environments.
    }
  }
};

export const subscribeToAuthTokenChanges = (listener, options = {}) => {
  if (typeof listener !== 'function') return () => {};

  authTokenListeners.add(listener);
  if (options.emitCurrent === true) {
    listener({
      accessToken: getAccessToken(),
      refreshToken: getRefreshToken(),
      reason: 'current',
    });
  }

  return () => authTokenListeners.delete(listener);
};

export const onAuthTokenChange = subscribeToAuthTokenChanges;

export const setAuthTokens = (tokens = {}, options = {}) => {
  const storage = getStorage();
  if (!storage) {
    emitAuthTokenChange(options.reason || 'updated');
    return { accessToken: '', refreshToken: '' };
  }

  const tokenPayload = typeof tokens === 'string'
    ? { accessToken: tokens }
    : (tokens || {});
  const accessToken = tokenPayload.access_token ?? tokenPayload.accessToken;
  const refreshToken = tokenPayload.refresh_token ?? tokenPayload.refreshToken;
  const previousAccessToken = getAccessToken();
  const previousRefreshToken = getRefreshToken();

  if (accessToken !== undefined) {
    if (accessToken) storage.setItem(AUTH_TOKEN_KEY, String(accessToken));
    else storage.removeItem(AUTH_TOKEN_KEY);
  } else if (options.clearMissingAccess === true) {
    storage.removeItem(AUTH_TOKEN_KEY);
  }

  if (refreshToken !== undefined) {
    if (refreshToken) storage.setItem(REFRESH_TOKEN_KEY, String(refreshToken));
    else storage.removeItem(REFRESH_TOKEN_KEY);
  } else if (options.clearMissingRefresh === true) {
    storage.removeItem(REFRESH_TOKEN_KEY);
  }

  const nextAccessToken = getAccessToken();
  const nextRefreshToken = getRefreshToken();
  if (
    nextAccessToken !== previousAccessToken ||
    nextRefreshToken !== previousRefreshToken ||
    options.forceEvent === true
  ) {
    emitAuthTokenChange(options.reason || 'updated');
  }

  return {
    accessToken: nextAccessToken,
    refreshToken: nextRefreshToken,
  };
};

const platformApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 50000,
  headers: { 'Content-Type': 'application/json' },
});

export const clearAuthStorage = (reason = 'cleared') => {
  const storage = getStorage();
  const hadSession = Boolean(getAccessToken() || getRefreshToken());

  storage?.removeItem(AUTH_TOKEN_KEY);
  storage?.removeItem(REFRESH_TOKEN_KEY);
  storage?.removeItem('userData');
  storage?.removeItem('accountData');

  if (hadSession) emitAuthTokenChange(reason);
};

let refreshPromise = null;

const requestAccessTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Missing refresh token');

  const response = await axios.post(
    `${API_BASE_URL}/public/auth/refresh`,
    { refresh_token: refreshToken },
    {
      timeout: 50000,
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const data = response.data?.data || response.data || {};
  const accessToken = data.access_token || data.accessToken;
  const nextRefreshToken = data.refresh_token || data.refreshToken;

  if (!accessToken) throw new Error('Refresh response missing access token');

  setAuthTokens(
    { accessToken, refreshToken: nextRefreshToken },
    { reason: 'refresh' },
  );

  return accessToken;
};

export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = requestAccessTokenRefresh()
      .catch(error => {
        clearAuthStorage('refresh-failed');
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// Attach auth token to every request
platformApi.interceptors.request.use(config => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 globally
platformApi.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isAuthRequest = originalRequest?.url?.includes('/public/auth/');

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return platformApi(originalRequest);
      } catch (refreshError) {
        clearAuthStorage('refresh-failed');
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      clearAuthStorage('unauthorized');
    }

    return Promise.reject(error);
  },
);

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', event => {
    if (event.key === AUTH_TOKEN_KEY || event.key === REFRESH_TOKEN_KEY) {
      emitAuthTokenChange('storage');
    }
  });
}

export { API_BASE_URL, WS_BASE_URL };
export default platformApi;
